import { motion } from 'framer-motion';
import { Network, Cloud, BarChart3, Building2, Shield, Zap } from 'lucide-react';

const capabilities = [
  {
    icon: Network,
    title: 'Program & Project Management',
    description: 'Agile (Scrum, SAFe) program delivery for federal, state, and local initiatives. PMO setup, governance, performance tracking, and stakeholder alignment.',
    span: 'lg:col-span-8',
  },
  {
    icon: Cloud,
    title: 'Oracle Cloud & Enterprise Technology',
    description: 'Oracle Cloud (Financials, EPM - EDMCS, ARCS, EPBCS/EPCM) implementation. ERP modernization & system integrations.',
    span: 'lg:col-span-4',
  },
  {
    icon: BarChart3,
    title: 'Data & Analytics',
    description: 'Power BI dashboards, data governance, reporting systems, and sensitive data masking for regulatory compliance.',
    span: 'lg:col-span-4',
  },
  {
    icon: Building2,
    title: 'Utilities & Infrastructure Operations',
    description: 'Water meter program management (100k+ meter deployment). Field workforce coordination, tracking, and operational efficiency.',
    span: 'lg:col-span-4',
  },
  {
    icon: Shield,
    title: 'Logistics & Supply Chain Operations',
    description: 'Sensitive data masking & regulatory compliance. Audit readiness, control alignment, and process standardization.',
    span: 'lg:col-span-4',
  },
  {
    icon: Zap,
    title: 'Agile Transformation & SAFe Enablement',
    description: 'Certified Agile leadership (PMP, CSM, CSPO, PMI-ACP, SAFe SPC). Sprint-based delivery and continuous improvement culture.',
    span: 'lg:col-span-12',
  },
];

const cardVariant = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: [0.25, 0.4, 0.25, 1] }
  })
};

export default function Capabilities() {
  return (
    <section
      id="capabilities"
      data-testid="capabilities-section"
      className="py-24 lg:py-32 bg-[#141414]"
    >
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <p className="font-body text-[#B8102E] text-xs tracking-[0.3em] uppercase mb-4">
            What We Deliver
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
            Core Capabilities
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return (
              <motion.div
                key={cap.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={cardVariant}
                className={`${cap.span} group`}
              >
                <div
                  data-testid={`capability-card-${i}`}
                  className="h-full p-6 lg:p-8 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg hover:border-[#B8102E] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(184,16,46,0.08)]"
                >
                  <Icon className="h-8 w-8 text-[#B8102E] mb-4" />
                  <h3 className="font-heading text-lg font-semibold text-white mb-2">
                    {cap.title}
                  </h3>
                  <p className="font-body text-sm text-[#A3A3A3] leading-relaxed">
                    {cap.description}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {['Speed', 'Accuracy', 'Gov Standards'].map((tag) => (
                      <span
                        key={tag}
                        className="text-[10px] font-body uppercase tracking-wider text-[#737373] border border-[#2A2A2A] rounded px-2 py-0.5 group-hover:border-[#B8102E]/30 transition-colors"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
